/**
 * Stacked time-series by California place (County or City) from ghg_inventory_precalculated.
 * Cities: smallest 300 emitters (by cumulative Mt for the chosen sector over the year window) → "Other (300 cities)".
 *
 * Dedupes rows that share the same canonical geofips (leading-zero variants) per year, keeping the newest
 * calculation_date so stacked totals are not inflated by duplicate keys in the table.
 */

import {
  canonicalInventoryGeofipsKey,
  inventoryGeofipsNumeric,
  preferNewerPrecalcRow,
} from './ghgInventoryGeofipsCanonical';
import { supabaseClient } from './supabase';

const PAGE_SIZE = 1000;
export const CA_STACKED_OTHER_CITIES_COUNT = 300;

export type CaliforniaGeoStackedGeography = 'cities' | 'counties';

export type CaliforniaGeoStackedSector =
  | 'total'
  | 'transportation'
  | 'electricity'
  | 'commercial_residential'
  | 'agriculture'
  | 'waste_other';

export const CALIFORNIA_GEO_STACKED_SECTOR_OPTIONS: {
  value: CaliforniaGeoStackedSector;
  label: string;
}[] = [
  { value: 'total', label: 'Total' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'electricity', label: 'Electricity' },
  { value: 'commercial_residential', label: 'Commercial & residential (natural gas)' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'waste_other', label: 'Waste & other' },
];

type RawRow = {
  geofips: unknown;
  year: number;
  location_name: string | null;
  calculation_date: string | null;
  total_emissions: unknown;
  ldv_emissions: unknown;
  hdv_emissions: unknown;
  elec_total_emissions: unknown;
  ng_total_emissions: unknown;
  waste_emissions: unknown;
  offroad_emissions: unknown;
  agriculture_emissions: unknown;
};

export interface CaliforniaGeoStackedFetchOptions {
  geography: CaliforniaGeoStackedGeography;
  sector: CaliforniaGeoStackedSector;
  yearMin: number;
  yearMax: number;
}

export interface CaliforniaGeoStackedPayload {
  years: number[];
  stackKeys: string[];
  rows: Record<string, string | number>[];
  otherBucketLabel: string | null;
  error: string | null;
}

function n(v: unknown): number {
  if (v == null) return 0;
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function sectorMt(row: RawRow, sector: CaliforniaGeoStackedSector): number {
  const ldv = n(row.ldv_emissions);
  const hdv = n(row.hdv_emissions);
  const elec = n(row.elec_total_emissions);
  const ng = n(row.ng_total_emissions);
  const waste = n(row.waste_emissions);
  const off = n(row.offroad_emissions);
  const ag = n(row.agriculture_emissions);
  const total = n(row.total_emissions);

  switch (sector) {
    case 'total':
      return total;
    case 'transportation':
      return ldv + hdv;
    case 'electricity':
      return elec;
    case 'commercial_residential':
      return ng;
    case 'agriculture':
      return ag;
    case 'waste_other':
      return waste + off;
    default:
      return total;
  }
}

function pickLabel(prev: string | undefined, next: string): string {
  const p = (prev || '').trim();
  const t = next.trim();
  if (!t) return p;
  if (!p) return t;
  return t.length > p.length ? t : p;
}

const OTHER_LABEL = 'Other (300 cities)';

export async function fetchCaliforniaGeoStackedInventory(
  options: CaliforniaGeoStackedFetchOptions
): Promise<CaliforniaGeoStackedPayload> {
  const geoType = options.geography === 'counties' ? 'County' : 'City';
  const raw: RawRow[] = [];
  let from = 0;

  for (;;) {
    let q = supabaseClient
      .from('ghg_inventory_precalculated')
      .select(
        'geofips,year,location_name,calculation_date,total_emissions,ldv_emissions,hdv_emissions,elec_total_emissions,ng_total_emissions,waste_emissions,offroad_emissions,agriculture_emissions'
      )
      .eq('geography_type', geoType)
      .gte('year', options.yearMin)
      .lte('year', options.yearMax)
      .order('geofips', { ascending: true })
      .order('year', { ascending: true });

    if (geoType === 'County') {
      q = q.gte('geofips', 6000).lt('geofips', 7000);
    } else {
      q = q.gte('geofips', 600000).lt('geofips', 700000);
    }

    const { data, error } = await q.range(from, from + PAGE_SIZE - 1);
    if (error) {
      return {
        years: [],
        stackKeys: [],
        rows: [],
        otherBucketLabel: null,
        error: error.message,
      };
    }

    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      raw.push({
        geofips: r.geofips,
        year: Number(r.year),
        location_name: r.location_name != null ? String(r.location_name) : null,
        calculation_date: r.calculation_date != null ? String(r.calculation_date) : null,
        total_emissions: r.total_emissions,
        ldv_emissions: r.ldv_emissions,
        hdv_emissions: r.hdv_emissions,
        elec_total_emissions: r.elec_total_emissions,
        ng_total_emissions: r.ng_total_emissions,
        waste_emissions: r.waste_emissions,
        offroad_emissions: r.offroad_emissions,
        agriculture_emissions: r.agriculture_emissions,
      });
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  /** One row per (canonical geofips, year): newest calculation_date wins. */
  const cell = new Map<string, RawRow>();
  for (const r of raw) {
    if (!Number.isFinite(r.year)) continue;
    const name = (r.location_name || '').trim().toLowerCase();
    if (name === 'california') continue;
    const canon = canonicalInventoryGeofipsKey(r.geofips);
    if (!canon) continue;
    const key = `${canon}\t${r.year}`;
    const prev = cell.get(key);
    cell.set(key, prev ? preferNewerPrecalcRow(prev, r) : r);
  }

  const yearSet = new Set<number>();
  const geofipsToLabel = new Map<number, string>();
  const triple = new Map<number, Map<number, RawRow>>();

  for (const r of Array.from(cell.values())) {
    const gfNum = inventoryGeofipsNumeric(r.geofips);
    if (gfNum == null || !Number.isFinite(r.year)) continue;
    yearSet.add(r.year);
    geofipsToLabel.set(gfNum, pickLabel(geofipsToLabel.get(gfNum), (r.location_name || '').trim()));
    if (!triple.has(gfNum)) triple.set(gfNum, new Map());
    triple.get(gfNum)!.set(r.year, r);
  }

  let years = Array.from(yearSet).sort((a, b) => a - b);
  years = years.filter((y) => y >= options.yearMin && y <= options.yearMax);

  if (years.length === 0 || triple.size === 0) {
    return {
      years: [],
      stackKeys: [],
      rows: [],
      otherBucketLabel: null,
      error: null,
    };
  }

  const geofipsList = Array.from(triple.keys());

  const labelCounts = new Map<string, number>();
  for (const gf of geofipsList) {
    const base = (geofipsToLabel.get(gf) || `Geofips ${gf}`).trim() || `Geofips ${gf}`;
    labelCounts.set(base, (labelCounts.get(base) || 0) + 1);
  }

  const uniqueStackLabel = (gf: number): string => {
    const base = (geofipsToLabel.get(gf) || `Geofips ${gf}`).trim() || `Geofips ${gf}`;
    if ((labelCounts.get(base) || 0) > 1) return `${base} (${gf})`;
    return base;
  };

  const cumulative = (gf: number): number =>
    years.reduce((s, y) => {
      const row = triple.get(gf)?.get(y);
      return s + (row ? sectorMt(row, options.sector) : 0);
    }, 0);

  const ranked = [...geofipsList].sort((a, b) => cumulative(b) - cumulative(a));

  let topGeofips: number[];
  let bottomGeofips: number[] = [];
  let otherBucketLabel: string | null = null;

  if (geoType === 'City' && ranked.length > CA_STACKED_OTHER_CITIES_COUNT) {
    topGeofips = ranked.slice(0, ranked.length - CA_STACKED_OTHER_CITIES_COUNT);
    bottomGeofips = ranked.slice(ranked.length - CA_STACKED_OTHER_CITIES_COUNT);
    otherBucketLabel = OTHER_LABEL;
  } else {
    topGeofips = ranked;
  }

  const stackKeys = [
    ...topGeofips.map((gf) => uniqueStackLabel(gf)),
    ...(otherBucketLabel ? [otherBucketLabel] : []),
  ];

  const rows: Record<string, string | number>[] = years.map((year) => {
    const row: Record<string, string | number> = { year };
    for (const gf of topGeofips) {
      const label = uniqueStackLabel(gf);
      const r = triple.get(gf)?.get(year);
      row[label] = r ? Math.round(sectorMt(r, options.sector)) : 0;
    }
    if (otherBucketLabel) {
      let sum = 0;
      for (const gf of bottomGeofips) {
        const r = triple.get(gf)?.get(year);
        if (r) sum += sectorMt(r, options.sector);
      }
      row[otherBucketLabel] = Math.round(sum);
    }
    return row;
  });

  return {
    years,
    stackKeys,
    rows,
    otherBucketLabel,
    error: null,
  };
}
